import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { DIGITAL_JOURNEY_BASE_URL } from '../apiConfig';
import moment from 'moment';

// Async thunk for uploading digital lead file
export const uploadDigitalLeadFile = createAsyncThunk(
  'digitalLeads/uploadFile',
  async ({ file, jwtBearer }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${DIGITAL_JOURNEY_BASE_URL}digital/upload/file/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${jwtBearer}`
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for fetching digital leads
export const fetchDigitalLeads = createAsyncThunk(
  'digitalLeads/fetchLeads',
  async ({ userid, userRole, page, limit, jwtBearer }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${DIGITAL_JOURNEY_BASE_URL}digital/leads/`,
        {
          params: { userid, userRole, page, limit },
          headers: {
            Authorization: `Bearer ${jwtBearer}`
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchLeadDetails = createAsyncThunk(
  'digitalLeads/fetchLeadDetails',
  async ({ objectId, jwtBearer }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${DIGITAL_JOURNEY_BASE_URL}digital/get/${objectId}/details`,
        {
          headers: {
            Authorization: `Bearer ${jwtBearer}`
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateLeadDetails = createAsyncThunk(
  'digitalLeads/updateLead',
  async ({ objectId, updateData, jwtBearer }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${DIGITAL_JOURNEY_BASE_URL}digital/update/${objectId}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${jwtBearer}`,
            'Content-Type': 'application/json',
          }
        }
      );

      return { objectId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const digitalLeadsSlice = createSlice({
  name: 'digitalLeads',
  initialState: {
    leads: [],
    currentLead: null,
    meta: {
      page: 1,
      limit: 10,
      totalPages: 0,
      totalCount: 0
    },
    loading: false,
    error: null,
    uploadStatus: 'idle',
    lastUploadTime: null
  },
  reducers: {
    resetUploadStatus: (state) => {
      state.uploadStatus = 'idle';
      state.error = null;
    },
    clearCurrentLead: (state) => {
      state.currentLead = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Upload File
      .addCase(uploadDigitalLeadFile.pending, (state) => {
        state.uploadStatus = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadDigitalLeadFile.fulfilled, (state, action) => {
        state.uploadStatus = 'succeeded';
        state.loading = false;
        state.lastUploadTime = moment().format();
      })
      .addCase(uploadDigitalLeadFile.rejected, (state, action) => {
        state.uploadStatus = 'failed';
        state.loading = false;
        state.error = action.payload || 'Failed to upload file';
      })

      // Fetch Leads
      .addCase(fetchDigitalLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDigitalLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchDigitalLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch leads';
      })

      // Fetch Lead Details
      .addCase(fetchLeadDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeadDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLead = action.payload.data;
      })
      .addCase(fetchLeadDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch lead details';
      })

      // Update Lead Details
      .addCase(updateLeadDetails.pending, (state) => {
        state.updateStatus = 'loading';
        state.error = null;
      })
      .addCase(updateLeadDetails.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        // Update current lead in state
        if (state.currentLead && state.currentLead._id === action.payload.objectId) {
          state.currentLead = { ...state.currentLead, ...action.payload.data };
        }
        // Update in leads list if present
        const index = state.leads.findIndex(lead => lead._id === action.payload.objectId);
        if (index !== -1) {
          state.leads[index] = { ...state.leads[index], ...action.payload.data };
        }
      })
      .addCase(updateLeadDetails.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.error = action.payload;
      });

  }
});

export const { resetUploadStatus, clearCurrentLead, resetUpdateStatus } = digitalLeadsSlice.actions;
export default digitalLeadsSlice.reducer;